/**
 * Getting around the office (docs/refactor/29): a dropdown with every place, and an arrow at the
 * far left and the far right of every room — the previous and the next place, wrapping.
 *
 * The same two controls on every room (the hall, the kitchen, the war room and the Bug Hunt), so a
 * person learns them once. Going to an area of the floor from the floor changes the area in place;
 * from the Bug Hunt it opens the floor on that area.
 *
 * And, on your own character's right-click menu, "Ir para…": walk your character to another area.
 */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { PLACES, PLACE_KEYS, ROOM_AREAS, stepPlace, type Place } from './office'
import type { RoomArea } from '../lib/types'
import '../styles/office.css'

/** The dropdown: every place of the office, the current one chosen. */
export function OfficePicker({ current, onGo }: { current: Place; onGo: (place: Place) => void }) {
  const { t } = useI18n()
  return (
    <select
      className="select select--sm office-picker"
      value={current}
      onChange={(event) => onGo(event.target.value as Place)}
      aria-label={t('office.place')}
      title={t('office.place')}
    >
      {PLACES.map((place) => (
        <option key={place} value={place}>{t(PLACE_KEYS[place])}</option>
      ))}
    </select>
  )
}

/**
 * An arrow at each side of the room, the name of where it leads in its tooltip. Placed by the room
 * that holds it (a positioned stage); `inset` moves the left one clear of what already sits on
 * that edge, such as the Bug Hunt's counters.
 */
export function OfficeArrows({ current, onGo, inset = 0 }: { current: Place; onGo: (place: Place) => void; inset?: number }) {
  const { t } = useI18n()
  const previous = stepPlace(current, -1)
  const next = stepPlace(current, 1)
  const previousLabel = t('office.prev', { name: t(PLACE_KEYS[previous]) })
  const nextLabel = t('office.next', { name: t(PLACE_KEYS[next]) })

  return (
    <>
      <button
        type="button"
        className="office-arrow office-arrow--left"
        style={inset ? { left: inset } : undefined}
        onClick={() => onGo(previous)}
        aria-label={previousLabel}
        title={previousLabel}
      >
        <ChevronLeft size={20} aria-hidden />
      </button>
      <button type="button" className="office-arrow office-arrow--right" onClick={() => onGo(next)} aria-label={nextLabel} title={nextLabel}>
        <ChevronRight size={20} aria-hidden />
      </button>
    </>
  )
}

/**
 * "Ir para…" on your own character's menu: the areas, the one you are in marked.
 *
 * Drawn into the person menu that is open (components/PersonMenu) rather than as a second menu
 * beside it: one menu per right-click.
 */
export function AreaMenuItems({ current, onMove }: { current?: RoomArea; onMove: (area: RoomArea) => void }) {
  const { t } = useI18n()
  return (
    <>
      <div className="bug-menu__sep" />
      <span className="office-menu__title tiny muted">{t('office.goTo')}</span>
      {ROOM_AREAS.map((area) => (
        <button
          key={area}
          type="button"
          role="menuitem"
          className="bug-menu__item"
          disabled={area === current}
          aria-current={area === current ? 'location' : undefined}
          onClick={() => onMove(area)}
        >
          {t(PLACE_KEYS[area])}
          {area === current && <span className="tiny muted">· {t('office.here')}</span>}
        </button>
      ))}
    </>
  )
}

/**
 * Walks your own character to an area: the server keeps where it stands, the room's people are read
 * again so it moves, and `arrive` takes the viewer there too.
 */
export function useWalkTo(arrive: (area: RoomArea) => void) {
  const queryClient = useQueryClient()
  return useCallback(
    async (area: RoomArea) => {
      await api.room.setRoomArea(area)
      await queryClient.invalidateQueries({ queryKey: ['room-activity'] })
      arrive(area)
    },
    [queryClient, arrive],
  )
}
